/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict';
import { useState, useEffect, useContext, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, ProgressBar, Stack, Table } from 'react-bootstrap';
import {
    checkForUpdates, downloadUpdate, launchInstaller, applyLinuxUpdate,
    checkUpdateNotifications, getSetting, setSetting,
    UpdateEntry, UpdateInfo,
} from '../../data';
import { MetadataContext } from '../MetadataContext';
import { SettingsContext } from '../SettingsContext';
import { StatusContext } from '../StatusContext';
import { WsContext, WsContextType } from '../WsContext';
import { formatBytes } from '../../utils/formatBytes';
import { compareSemver, extractSemverFromFilename, extractSemverFromText, parseSemver } from '../../utils/versioning';
import { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';

type UpdateWithRelease = UpdateEntry & {
    release: string;
    arch: string;
    key: string;
};

function UpdatesSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const mutedText = theme?.colors?.textMuted || '#9aa0a6';

    // ---- firmware update state ----
    const [updates, setUpdates] = useState<UpdateInfo>({ updates: [] });
    const [selectedUpdateKey, setSelectedUpdateKey] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isCheckingUpdates, setIsCheckingUpdates] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [downloadSuccess, setDownloadSuccess] = useState(false);
    const [downloadProgressNow, setDownloadProgressNow] = useState(0);
    const [downloadProgressLabel, setDownloadProgressLabel] = useState('');
    const [downloadBytesTransferred, setDownloadBytesTransferred] = useState(0);
    const [downloadTotalBytes, setDownloadTotalBytes] = useState<number | null>(null);
    const [activeDownloadProgressId, setActiveDownloadProgressId] = useState<string | null>(null);
    const [showScrollHint, setShowScrollHint] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const updatesScrollRef = useRef<HTMLDivElement | null>(null);

    // ---- notification settings state ----
    const [isCheckingNotifications, setIsCheckingNotifications] = useState(false);
    const [notificationCheckResult, setNotificationCheckResult] = useState<string>('');
    const [fpEnabled, setFpEnabled] = useState<boolean>(true);
    const [fpIntervalMin, setFpIntervalMin] = useState<string>('60');
    const [fwEnabled, setFwEnabled] = useState<boolean>(true);
    const [fwIntervalMin, setFwIntervalMin] = useState<string>('60');
    const [updateSettingsRefresh, setUpdateSettingsRefresh] = useState(0);

    const metadataContext = useContext<any>(MetadataContext);
    const wsContext = useContext<WsContextType>(WsContext);
    const settingsContext = useContext(SettingsContext);
    const statusContext = useContext<any>(StatusContext);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        settingsContext.setIsDirty(false);
    }, [settingsContext]);

    // auto-trigger check when navigated with ?checkNow=1
    useEffect(() => {
        if (searchParams.get('checkNow') === '1') {
            ucheck();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // load notification settings
    useEffect(() => {
        async function load() {
            try {
                const fp = await getSetting('/updates/fingerprints/enabled').catch(() => 'true');
                setFpEnabled(fp !== 'false');
                const fpMin = await getSetting('/updates/fingerprints/interval_min').catch(() => '60');
                setFpIntervalMin(fpMin || '60');
                const fw = await getSetting('/updates/firmware/enabled').catch(() => 'true');
                setFwEnabled(fw !== 'false');
                const fwMin = await getSetting('/updates/firmware/interval_min').catch(() => '60');
                setFwIntervalMin(fwMin || '60');
            } catch (_e) { /* ignore */ }
        }
        load();
    }, [updateSettingsRefresh]);

    // WS: backend upgrade event
    useEffect(() => {
        if (!wsContext.ws) return;
        const handler = () => {
            setIsUpgrading(true);
            setIsDownloading(false);
            // The applyLinuxUpdate RPC call may hang forever because socket.io drops
            // the ack callback when the server shuts down. Drive the poll+reload from
            // here so the page reloads once the new server is healthy.
            pollForReconnect().then(() => window.location.reload());
        };
        wsContext.ws.on('upgrading', handler);
        return () => { wsContext.ws.off('upgrading', handler); };
    }, [wsContext.ws]);

    // WS: download progress
    useEffect(() => {
        if (!wsContext.ws) return;
        const handler = (msg: any) => {
            if (!activeDownloadProgressId) return;
            if (msg?.progressId !== activeDownloadProgressId) return;
            if (msg?.op !== 'update-download' && msg?.op !== 'update-unpack') return;
            if (typeof msg?.now === 'number') setDownloadProgressNow(Math.max(0, Math.min(100, msg.now)));
            if (typeof msg?.bytesTransferred === 'number') setDownloadBytesTransferred(Math.max(0, msg.bytesTransferred));
            if (typeof msg?.totalBytes === 'number' && msg.totalBytes > 0) setDownloadTotalBytes(msg.totalBytes);
            setDownloadProgressLabel(msg?.label || 'Downloading...');
        };
        wsContext.ws.on('fileTransferProgress', handler);
        return () => { wsContext.ws.off('fileTransferProgress', handler); };
    }, [wsContext.ws, activeDownloadProgressId]);

    // scroll hint
    useEffect(() => {
        const timer = setTimeout(refreshScrollHint, 0);
        window.addEventListener('resize', refreshScrollHint);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', refreshScrollHint);
        };
    }, [updates, isCheckingUpdates]);

    // ---- firmware helper functions ----
    function isLinuxProdDeployment(): boolean {
        return metadataContext?.deploymentType === 'linux-prod';
    }

    function isWebDeployment(): boolean {
        return metadataContext?.deploymentType === 'web';
    }

    function getOsReleasePath(): string {
        if (isLinuxProdDeployment() || isWebDeployment()) return 'releases/linux-prod';
        if (typeof navigator === 'undefined') return '';
        const nav = navigator as any;
        const platform = (nav.userAgentData?.platform || navigator.platform || '').toLowerCase();
        if (platform.includes('mac')) return 'releases/darwin';
        if (platform.includes('win')) return 'releases/win32';
        return '';
    }

    function isMacClient(): boolean { return getOsReleasePath() === 'releases/darwin'; }

    function isElectronMode(): boolean {
        return typeof window !== 'undefined' && !!(window as any).electron;
    }

    function isAllowedInstaller(name: string): boolean {
        const lower = name.toLowerCase();
        if (isLinuxProdDeployment() || isWebDeployment()) return lower.endsWith('.tar.gz');
        return lower.endsWith('.dmg') || lower.endsWith('.exe');
    }

    function formatModifiedDate(modified: number): string {
        const date = new Date(modified);
        if (Number.isNaN(date.getTime())) return String(modified);
        return date.toLocaleString();
    }

    function getUpdateKey(update: UpdateEntry): string { return `${update.name}|${update.modified}|${update.size}`; }
    function getDisplayName(name: string): string { return name.split(/[\\/]/).pop() || name; }

    function extractArchitectureFromFilename(name: string): string {
        const lower = name.toLowerCase();
        const archMatch = lower.match(/(?:^|[._-])(arm64|x64|ia32|x86_64|aarch64|universal)(?:[._-]|$)/);
        if (!archMatch) return 'unknown';
        const arch = archMatch[1];
        if (arch === 'x86_64') return 'x64';
        if (arch === 'aarch64') return 'arm64';
        return arch;
    }

    const osReleasePath = getOsReleasePath();
    const filteredUpdates: UpdateWithRelease[] = updates.updates.filter((u: UpdateEntry) => {
        const lower = u.name.toLowerCase();
        return osReleasePath ? lower.includes(osReleasePath) && isAllowedInstaller(lower) : false;
    }).map((u: UpdateEntry) => ({
        ...u,
        release: extractSemverFromFilename(u.name) || 'unknown',
        arch: extractArchitectureFromFilename(u.name),
        key: getUpdateKey(u),
    })).sort((a: UpdateWithRelease, b: UpdateWithRelease) => {
        const v = compareSemver(b.release, a.release);
        if (v !== 0) return v;
        const m = b.modified - a.modified;
        if (m !== 0) return m;
        return a.name.localeCompare(b.name);
    });

    useEffect(() => {
        if (filteredUpdates.length === 0) { setSelectedUpdateKey(null); return; }
        const best = filteredUpdates.reduce((cur: UpdateWithRelease, u: UpdateWithRelease) =>
            compareSemver(u.release, cur.release) > 0 ? u : cur, filteredUpdates[0]);
        setSelectedUpdateKey(best.key);
    }, [updates, osReleasePath]);

    const selectedUpdate = filteredUpdates.find(u => u.key === selectedUpdateKey) || null;
    const currentRunningRelease = extractSemverFromText(metadataContext?.appVersion);
    const selectedVsCurrent = (selectedUpdate && currentRunningRelease)
        ? compareSemver(selectedUpdate.release, currentRunningRelease) : null;
    const isSelectedCurrentVersion = selectedVsCurrent === 0;
    const isDowngradeCandidate = selectedVsCurrent !== null && selectedVsCurrent < 0;
    const installButtonVariant = (isSelectedCurrentVersion || isDowngradeCandidate) ? 'outline-primary' : 'primary';

    const bestUpdate = filteredUpdates.length > 0
        ? filteredUpdates.reduce((cur: UpdateWithRelease, u: UpdateWithRelease) => {
            const cp = parseSemver(u.release);
            const bp = parseSemver(cur.release);
            if (cp && bp) {
                const ci = cp.prerelease.length > 0;
                const bi = bp.prerelease.length > 0;
                if (!ci && bi) return u;
                if (ci && !bi) return cur;
            }
            return compareSemver(u.release, cur.release) > 0 ? u : cur;
        }, filteredUpdates[0])
        : null;

    function getUpdateStatus(update: UpdateWithRelease) {
        if (!currentRunningRelease) return null;
        const c = compareSemver(update.release, currentRunningRelease);
        if (c === 0) return 'current';
        if (c < 0) return 'downgrade';
        return 'upgrade';
    }

    function getUpdateRelationshipBadge(update: UpdateWithRelease) {
        const status = getUpdateStatus(update);
        if (!status) return null;
        const badges: Record<string, { color: string; label: string }> = {
            'upgrade': { color: 'success', label: 'upgrade' },
            'downgrade': { color: 'danger', label: 'downgrade' },
            'current': { color: 'primary', label: 'already running' },
        };
        return badges[status] || null;
    }

    function getPrereleaseType(version: string): string | null {
        const p = parseSemver(version);
        if (!p || p.prerelease.length === 0) return null;
        const tag = p.prerelease[0].toLowerCase();
        if (tag.includes('alpha')) return 'alpha';
        if (tag.includes('beta')) return 'beta';
        if (tag.includes('rc')) return 'rc';
        if (tag.includes('dev')) return 'dev';
        return 'pre';
    }

    function getPrereleaseLabel(type: string | null): string {
        if (!type) return '';
        return ({ alpha: 'Alpha', beta: 'Beta', rc: 'RC', dev: 'Dev', pre: 'Prerelease' } as Record<string, string>)[type] || 'Prerelease';
    }

    function getPrereleaseBadgeColor(type: string | null): string {
        if (!type) return 'info';
        return ({ alpha: 'danger', beta: 'warning', rc: 'success', dev: 'secondary', pre: 'info' } as Record<string, string>)[type] || 'info';
    }

    function shouldShowRecommended(update: UpdateWithRelease): boolean {
        if (!bestUpdate || update.key !== bestUpdate.key) return false;
        if (!currentRunningRelease) return true;
        return compareSemver(update.release, currentRunningRelease) > 0;
    }

    function refreshScrollHint() {
        const el = updatesScrollRef.current;
        if (!el) { setShowScrollHint(false); return; }
        const hasOverflow = el.scrollHeight > (el.clientHeight + 1);
        const atBottom = (el.scrollTop + el.clientHeight) >= (el.scrollHeight - 1);
        setShowScrollHint(hasOverflow && !atBottom);
    }

    async function ucheck() {
        try {
            setIsCheckingUpdates(true);
            const response = await checkForUpdates();
            setUpdates(response);
        } catch (err: any) {
            console.error('Error checking for updates:', err);
        } finally {
            setIsCheckingUpdates(false);
        }
    }

    async function pollForReconnect(): Promise<void> {
        for (;;) {
            await new Promise(r => setTimeout(r, 2000));
            try {
                const res = await fetch('/healthz');
                if (res.ok) return;
            } catch { /* keep polling */ }
        }
    }

    async function handleDownloadAndInstall() {
        if (!selectedUpdate) { setDownloadError('No update selected.'); return; }
        const progressId = crypto.randomUUID();
        setIsDownloading(true);
        setDownloadError(null);
        setDownloadSuccess(false);
        setDownloadProgressNow(0);
        setDownloadProgressLabel('Preparing download...');
        setDownloadBytesTransferred(0);
        setDownloadTotalBytes(null);
        setActiveDownloadProgressId(progressId);
        try {
            const installerPath = await downloadUpdate(selectedUpdate.name, true, progressId);
            await launchInstaller(installerPath);
            setDownloadSuccess(true);
            setIsDownloading(false);
            setDownloadProgressNow(100);
            setTimeout(() => setDownloadSuccess(false), 5000);
        } catch (err: any) {
            setDownloadError(err.message || 'Failed to download and install update');
            setIsDownloading(false);
        }
    }

    async function handleLinuxUpdate() {
        if (!selectedUpdate) { setDownloadError('No update selected.'); return; }
        const progressId = crypto.randomUUID();
        setIsDownloading(true);
        setDownloadError(null);
        setDownloadProgressNow(0);
        setDownloadProgressLabel('Preparing download…');
        setDownloadBytesTransferred(0);
        setDownloadTotalBytes(null);
        setActiveDownloadProgressId(progressId);
        try {
            await applyLinuxUpdate(selectedUpdate.name, progressId);
            // Happy path: RPC acked before server shut down. The 'upgrading' WS event
            // handler already drives pollForReconnect+reload, but run it here too in
            // case the event arrived after this await resolved.
            setIsUpgrading(true);
            setIsDownloading(false);
        } catch (err: any) {
            // If the 'upgrading' WS event already fired (isUpgrading will be set by
            // that handler), the server is going down and the event handler drives the
            // reload — don't show an error or clear the upgrading state.
            // Only surface errors that happen before the upgrade started.
            setIsDownloading(false);
            if (!isUpgrading) {
                setDownloadError(err.message || 'Failed to apply update');
            }
        }
    }

    // ---- notification settings functions ----
    async function runCheckNotifications() {
        setIsCheckingNotifications(true);
        setNotificationCheckResult('');
        try {
            const rc = await checkUpdateNotifications();
            const parts: string[] = [];
            if (rc.fingerprints) {
                parts.push(`Fingerprints: ${rc.fingerprints.available ? `update available (ser_${rc.fingerprints.latestSerial}.fbot)` : 'up to date'}`);
            } else {
                parts.push('Fingerprints: check skipped or disabled');
            }
            if (rc.firmware) {
                parts.push(`Firmware: ${rc.firmware.updates.length} release(s) found`);
            } else {
                parts.push('Firmware: check skipped or disabled');
            }
            if (rc.errors.length > 0) parts.push(`Errors: ${rc.errors.join('; ')}`);
            setNotificationCheckResult(parts.join(' | '));
        } catch (err: any) {
            setNotificationCheckResult(`Check failed: ${err.message}`);
            statusContext.postError(`Update check failed: ${err.message}`);
        } finally {
            setIsCheckingNotifications(false);
        }
    }

    async function saveUpdateSettings() {
        try {
            await setSetting('/updates/fingerprints/enabled', fpEnabled ? 'true' : 'false', 'Enable periodic fingerprint update checks (true|false)');
            await setSetting('/updates/fingerprints/interval_min', fpIntervalMin || '60', 'Fingerprint update check interval in minutes');
            await setSetting('/updates/firmware/enabled', fwEnabled ? 'true' : 'false', 'Enable periodic firmware update checks (true|false)');
            await setSetting('/updates/firmware/interval_min', fwIntervalMin || '60', 'Firmware update check interval in minutes');
            setUpdateSettingsRefresh(n => n + 1);
            statusContext.postInfo('Update notification settings saved.');
        } catch (err: any) {
            statusContext.postError(`Failed to save update settings: ${err.message}`);
        }
    }

    return (
        <div>
            <style>{`@keyframes updatesUpgradeSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <div className="mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <h3 className="mb-1">Updates</h3>
                <div className="text-muted">Firmware update checking and notification settings.</div>
            </div>

            {/* ---- Firmware Updates ---- */}
            <div className="fw-semibold mb-2">Firmware</div>
            {filteredUpdates.length > 0 ? (
                <>
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <span>Available Versions</span>
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => ucheck()}
                                disabled={isCheckingUpdates}
                                style={{ padding: '0.25rem 0.5rem', color: theme?.colors?.ctext || '#0d6efd', textDecoration: 'none' }}
                                title="Refresh updates list"
                            >
                                <FontAwesomeIcon icon={faRotateRight} spin={isCheckingUpdates} />
                            </Button>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div style={{ position: 'relative' }}>
                                <div
                                    ref={updatesScrollRef}
                                    onScroll={refreshScrollHint}
                                    style={{ maxHeight: '20rem', overflowY: 'scroll', scrollbarGutter: 'stable' }}
                                >
                                    <Table striped hover className="mb-0">
                                        <thead>
                                            <tr>
                                                {['', 'Name', 'Release', 'Size', 'Modified Date'].map(h => (
                                                    <th key={h} style={{
                                                        position: 'sticky', top: 0, zIndex: 2,
                                                        backgroundColor: theme?.colors?.headerBg || '#f8f9fa',
                                                        width: h === '' ? '90px' : undefined,
                                                    }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUpdates.map((update: UpdateWithRelease) => (
                                                <tr
                                                    key={update.key}
                                                    className={selectedUpdate?.key === update.key ? 'table-active' : ''}
                                                    onClick={() => setSelectedUpdateKey(update.key)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>
                                                        {getUpdateRelationshipBadge(update) && (
                                                            <Badge pill bg={getUpdateRelationshipBadge(update)?.color}>
                                                                {getUpdateRelationshipBadge(update)?.label}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td>{getDisplayName(update.name)}</td>
                                                    <td>
                                                        {update.release}{' '}
                                                        {shouldShowRecommended(update) && <Badge pill bg="primary">Recommended</Badge>}{' '}
                                                        {getPrereleaseType(update.release) ? (
                                                            <Badge pill bg={getPrereleaseBadgeColor(getPrereleaseType(update.release))}>
                                                                {getPrereleaseLabel(getPrereleaseType(update.release))}
                                                            </Badge>
                                                        ) : <Badge pill bg="info">Release</Badge>}
                                                    </td>
                                                    <td>{update.size}</td>
                                                    <td>{formatModifiedDate(update.modified)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                {showScrollHint && (
                                    <div style={{
                                        position: 'absolute', left: 0, right: 0, bottom: 0, height: '1.75rem',
                                        pointerEvents: 'none', display: 'flex', justifyContent: 'center',
                                        alignItems: 'flex-end', fontSize: '0.75rem',
                                        color: theme?.colors?.textMuted || '#9aa0a6',
                                        background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.25))',
                                    }}>
                                        Scroll for more
                                    </div>
                                )}
                            </div>
                            <div className="d-flex align-items-center gap-3 p-3" style={{ borderTop: sectionBorder }}>
                                {isLinuxProdDeployment() || isWebDeployment() ? (
                                    <Button variant={installButtonVariant} onClick={() => handleLinuxUpdate()} disabled={!selectedUpdate || isDownloading || isUpgrading}>
                                        {isDownloading ? 'Downloading…' : isUpgrading ? 'Upgrading…' : 'Apply Update'}
                                    </Button>
                                ) : (
                                    <Button variant={installButtonVariant} onClick={() => handleDownloadAndInstall()} disabled={!selectedUpdate || isDownloading || !isElectronMode()}>
                                        {isDownloading ? 'Installing...' : 'Install Update'}
                                    </Button>
                                )}
                                <div>
                                    {selectedUpdate ? (
                                        <>
                                            {getUpdateRelationshipBadge(selectedUpdate) && (
                                                <Badge pill bg={getUpdateRelationshipBadge(selectedUpdate)?.color}>
                                                    {getUpdateRelationshipBadge(selectedUpdate)?.label}
                                                </Badge>
                                            )}{' '}
                                            {getDisplayName(selectedUpdate.name)}{' '}
                                            {shouldShowRecommended(selectedUpdate) && <Badge pill bg="primary">Recommended</Badge>}{' '}
                                            {getPrereleaseType(selectedUpdate.release) ? (
                                                <Badge pill bg={getPrereleaseBadgeColor(getPrereleaseType(selectedUpdate.release))}>
                                                    {getPrereleaseLabel(getPrereleaseType(selectedUpdate.release))}
                                                </Badge>
                                            ) : <Badge pill bg="info">Release</Badge>}
                                        </>
                                    ) : 'none'}
                                    {selectedUpdate && (isSelectedCurrentVersion || isDowngradeCandidate) && (
                                        <div className="small text-muted mt-1">Caution: Same Version or Downgrade</div>
                                    )}
                                </div>
                            </div>
                            {isDownloading && (
                                <div className="px-3 pb-3">
                                    <div className="small mb-1">{downloadProgressLabel || 'Downloading update...'}</div>
                                    <div className="small text-muted mb-1">
                                        {formatBytes(downloadBytesTransferred)} / {downloadTotalBytes ? formatBytes(downloadTotalBytes) : 'unknown'}
                                    </div>
                                    <ProgressBar
                                        now={downloadProgressNow}
                                        label={`${Math.round(downloadProgressNow)}%`}
                                        animated={downloadProgressNow > 0 && downloadProgressNow < 100}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                    {isUpgrading && (
                        <Alert variant="info" style={{ backgroundColor: theme?.colors?.headerBg || '#1f2937', color: theme?.colors?.text || '#f8fafc', borderColor: theme?.colors?.info || '#22d3ee' }}>
                            <div className="d-flex align-items-center gap-2">
                                <span aria-hidden="true" style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: '1.55rem', height: '1.55rem', borderRadius: '999px',
                                    backgroundColor: 'rgba(34,211,238,0.18)', border: `1px solid ${theme?.colors?.info || '#22d3ee'}`,
                                    color: theme?.colors?.text || '#f8fafc', fontSize: '0.85rem', fontWeight: 700, lineHeight: 1,
                                    animation: 'updatesUpgradeSpin 1.15s linear infinite', userSelect: 'none',
                                }} title="Upgrading">:)</span>
                                <span><strong>Upgrade in progress</strong> - waiting for server to come back online...</span>
                            </div>
                        </Alert>
                    )}
                    {downloadError && (
                        <Alert variant="danger" style={{ backgroundColor: '#3d2a2a', color: '#ff6b6b', borderColor: '#ff6b6b' }}>
                            <strong>Error:</strong> {downloadError}
                        </Alert>
                    )}
                    {downloadSuccess && (
                        <Alert variant="success" style={{ backgroundColor: '#2a3d2a', color: '#6bff6b', borderColor: '#6bff6b' }}>
                            {isMacClient() ? (
                                <><strong>Success!</strong> Update downloaded. The application will close, replace the current app in place, and restart automatically.</>
                            ) : (
                                <><strong>Success!</strong> Installer downloaded and launched. The application will close in a moment to allow the upgrade to complete. Follow the installation wizard to finish.</>
                            )}
                        </Alert>
                    )}
                </>
            ) : !isCheckingUpdates ? (
                <div className="text-center py-4 mb-4">
                    <p className="text-muted mb-3">No updates checked yet.</p>
                    <Button onClick={() => ucheck()} variant="primary">Check for Firmware Updates</Button>
                </div>
            ) : (
                <div className="text-center py-3 mb-4 text-muted">Checking...</div>
            )}

            {/* ---- Notification Settings ---- */}
            <div className="fw-semibold mb-2 mt-4" style={{ borderTop: sectionBorder, paddingTop: '1rem' }}>Update Notifications</div>
            <div className="text-muted small mb-3">
                Control automatic background checks for new fingerprint data and firmware releases.
                Status bar icons appear when updates are available.
                Changes take effect on the next scheduled check cycle.
            </div>
            <div className="mb-3">
                <div className="fw-semibold small mb-1">Fingerprints</div>
                <Stack direction="horizontal" gap={3} className="align-items-center flex-wrap">
                    <Form.Check
                        type="switch"
                        id="fp-enabled"
                        label="Enabled"
                        checked={fpEnabled}
                        onChange={e => setFpEnabled(e.target.checked)}
                    />
                    <Stack direction="horizontal" gap={2} className="align-items-center">
                        <Form.Label htmlFor="fp-interval" className="mb-0 small">Interval (min):</Form.Label>
                        <Form.Control
                            id="fp-interval"
                            type="number"
                            min={1}
                            style={{ width: 80 }}
                            value={fpIntervalMin}
                            onChange={e => setFpIntervalMin(e.target.value)}
                            disabled={!fpEnabled}
                        />
                    </Stack>
                </Stack>
            </div>
            <div className="mb-3">
                <div className="fw-semibold small mb-1">Firmware</div>
                <Stack direction="horizontal" gap={3} className="align-items-center flex-wrap">
                    <Form.Check
                        type="switch"
                        id="fw-enabled"
                        label="Enabled"
                        checked={fwEnabled}
                        onChange={e => setFwEnabled(e.target.checked)}
                    />
                    <Stack direction="horizontal" gap={2} className="align-items-center">
                        <Form.Label htmlFor="fw-interval" className="mb-0 small">Interval (min):</Form.Label>
                        <Form.Control
                            id="fw-interval"
                            type="number"
                            min={1}
                            style={{ width: 80 }}
                            value={fwIntervalMin}
                            onChange={e => setFwIntervalMin(e.target.value)}
                            disabled={!fwEnabled}
                        />
                    </Stack>
                </Stack>
            </div>
            <Stack direction="horizontal" gap={2} className="flex-wrap">
                <Button variant="outline-secondary" onClick={saveUpdateSettings}>
                    Save Settings
                </Button>
                <Button variant="primary" disabled={isCheckingNotifications} onClick={runCheckNotifications}>
                    {isCheckingNotifications ? 'Checking...' : 'Check Now'}
                </Button>
            </Stack>
            {notificationCheckResult && (
                <div className="mt-2 small" style={{
                    color: notificationCheckResult.includes('failed') || notificationCheckResult.includes('Error') ? '#dc3545' : mutedText,
                    fontFamily: 'monospace',
                }}>
                    {notificationCheckResult}
                </div>
            )}
        </div>
    );
}

export default UpdatesSettings;
